import React, { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useContracts, useClients, useTarifas, useComisionConfig, useConvenios } from "../../contexts/DataContext";
import { Heading } from "../../components/heading";
import { Text } from "../../components/text";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/table";
import { Badge } from "../../components/badge";
import { ChevronDownIcon } from "@heroicons/react/20/solid";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  ReferenceLine,
} from "recharts";
import { FianzaView } from "../../components/FianzaView";
import {
  getFechaEmision,
  getEmisorNombre,
  getSubRamo,
  getTipoFianza,
  formatNumeroFianza,
} from "../../utils/calculations";
import { useComisionLedger } from "../../hooks/useComisionLedger";
import { indexMovementsByFianza, resolveComision } from "../../utils/comisionReconcile";

const monthNames = [
  "enero", "febrero", "marzo", "abril", "mayo", "junio",
  "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre",
];

// Colors for chart bars based on tarifa ranges
const getTarifaColor = (tarifa, avgTarifa) => {
  if (tarifa >= avgTarifa * 1.2) return '#22c55e'; // green - above avg
  if (tarifa >= avgTarifa * 0.9) return '#284865'; // azul-surelia - around avg
  if (tarifa >= avgTarifa * 0.7) return '#f59e0b'; // amber - below avg
  return '#ef4444'; // red - much below avg
};

export function TarifasPorCliente({ selectedYear }) {
  const { contractsList, contractsLoading } = useContracts();
  const { clientsById, clientsLoading } = useClients();
  const { tarifas, tarifasLoading } = useTarifas();
  const { movements } = useComisionLedger();
  const { config: comisionConfig } = useComisionConfig();
  const { conveniosById } = useConvenios();
  const byFianza = useMemo(() => indexMovementsByFianza(movements), [movements]);

  const [selectedMonth, setSelectedMonth] = useState("all");
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [selectedFianza, setSelectedFianza] = useState(null);

  // Build fianzas with all needed data
  const allFianzas = useMemo(() => {
    return contractsList.flatMap((contract) => {
      // Convenio-split context so the on-the-fly fallback nets the same way
      // the ledger books (see comisionReconcile.resolveComision).
      const splitOpts = {
        contractData: contract,
        orgConfig: comisionConfig,
        convenioData: contract.convenioId ? conveniosById?.[contract.convenioId] : null,
      };
      // Igual que en Producción por cliente: los contratos de convenio no
      // tienen clientId y se agrupan por su convenio, no en una fila fantasma.
      const client = clientsById[contract.clientId];
      const clientName = contract.convenioId
        ? conveniosById?.[contract.convenioId]?.nombre || "Convenio sin nombre"
        : client?.nombreEmpresa || "Cliente sin nombre";
      const grupoId = contract.clientId
        || (contract.convenioId ? `conv:${contract.convenioId}` : "sin-cliente");

      return (contract.fianzas || [])
        .filter((fianza) => fianza.estadoFianza !== 'anulada' && fianza.estadoFianza !== 'sustituida') // anulada + sustituida excluded; cancelada retained
        .map((fianza) => {
        const inicio = getFechaEmision(fianza);
        const montoAfianzado = Number(fianza.montoAfianzado) || 0;
        const emisorNombre = getEmisorNombre(fianza);
        const fianzaSubRamo = getSubRamo(fianza);
        const fianzaTipo = getTipoFianza(fianza);

        // Ledger-net prima AND comisión when the fianza has movements; on-the-fly
        // otherwise — so the prima column matches the comisión beside it.
        const {
          prima: primaAmount,
          comisionPct,
          comisionMonto,
          primaMinima,
          tarifa: fianzaTarifa,
        } = resolveComision(
          fianza, byFianza.get(fianza.id), tarifas, emisorNombre, fianzaSubRamo, fianzaTipo, splitOpts
        );

        return {
          ...fianza,
          clientId: contract.clientId,
          grupoId,
          contratoId: contract.id,
          clientName,
          inicioVigenciaMs: inicio,
          montoAfianzado,
          primaAmount,
          fianzaTarifa,
          primaMinima,
          comisionPct,
          comisionMonto,
        };
      });
    });
  }, [contractsList, clientsById, tarifas, byFianza, comisionConfig, conveniosById]);

  // Filter fianzas by period
  const filteredFianzas = useMemo(() => {
    return allFianzas.filter((f) => {
      if (!f.inicioVigenciaMs) return false;
      // Only include fianzas with valid tarifa
      if (f.fianzaTarifa === null || f.fianzaTarifa === undefined) return false;
      const date = new Date(f.inicioVigenciaMs);
      const yr = date.getFullYear();
      const month = date.getMonth();
      if (yr !== selectedYear) return false;
      if (selectedMonth !== "all" && month !== selectedMonth) return false;
      return true;
    });
  }, [allFianzas, selectedYear, selectedMonth]);

  // Global average tarifa (excluding prima mínima)
  const globalAvgTarifa = useMemo(() => {
    const normal = filteredFianzas.filter((f) => !f.primaMinima);
    if (normal.length === 0) return 0;
    const sum = normal.reduce((acc, f) => acc + (f.fianzaTarifa || 0), 0);
    return sum / normal.length;
  }, [filteredFianzas]);

  // Aggregate by client (excluding prima mínima from tarifa calculations)
  const resumenPorCliente = useMemo(() => {
    const map = new Map();

    filteredFianzas.forEach((fianza) => {
      let entry = map.get(fianza.grupoId);
      if (!entry) {
        entry = {
          id: fianza.grupoId,
          name: fianza.clientName,
          fianzas: [],
          primaTotal: 0,
          montoTotal: 0,
          primaMinimaCount: 0,
        };
        map.set(fianza.grupoId, entry);
      }
      entry.fianzas.push(fianza);
      entry.primaTotal += fianza.primaAmount || 0;
      entry.montoTotal += fianza.montoAfianzado || 0;
      if (fianza.primaMinima) entry.primaMinimaCount += 1;
    });

    return Array.from(map.values())
      .map((entry) => {
        const normalFianzas = entry.fianzas.filter((f) => !f.primaMinima);
        const normalTarifas = normalFianzas.map((f) => f.fianzaTarifa || 0);
        const normalPrimaTotal = normalFianzas.reduce((acc, f) => acc + (f.primaAmount || 0), 0);

        return {
          ...entry,
          fianzasCount: entry.fianzas.length,
          avgTarifa: normalTarifas.length > 0 ? normalTarifas.reduce((a, b) => a + b, 0) / normalTarifas.length : 0,
          minTarifa: normalTarifas.length > 0 ? Math.min(...normalTarifas) : 0,
          maxTarifa: normalTarifas.length > 0 ? Math.max(...normalTarifas) : 0,
          weightedAvgTarifa: normalPrimaTotal > 0
            ? (normalFianzas.reduce((acc, f) => acc + (f.fianzaTarifa || 0) * (f.primaAmount || 0), 0) / normalPrimaTotal)
            : 0,
        };
      })
      .sort((a, b) => b.avgTarifa - a.avgTarifa);
  }, [filteredFianzas]);

  // Chart data - top 15 clients by fianzas count
  const chartData = useMemo(() => {
    return resumenPorCliente
      .slice(0, 15)
      .map((client) => ({
        name: client.name.length > 20 ? client.name.substring(0, 18) + "…" : client.name,
        fullName: client.name,
        avgTarifa: client.avgTarifa,
        fianzas: client.fianzasCount,
      }));
  }, [resumenPorCliente]);

  // Get fianzas for a client
  const getFianzasForClient = (clientId) => {
    return filteredFianzas
      .filter((f) => f.grupoId === clientId)
      .sort((a, b) => (b.fianzaTarifa || 0) - (a.fianzaTarifa || 0));
  };

  // Get client and contract data for FianzaView
  const getClientDataForFianza = (fianza) => {
    return clientsById[fianza.clientId] || {};
  };

  const getContractDataForFianza = (fianza) => {
    return contractsList.find((c) => c.id === fianza.contratoId) || {};
  };

  const toggleRowExpanded = (id) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const loading = contractsLoading || clientsLoading || tarifasLoading;

  const formatCurrency = (value) =>
    new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(value);

  const formatPercent = (value) => `${value.toFixed(2)}%`;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-3">
          <Heading level={1} className="text-3xl font-bold text-azul-surelia">
            Tarifas por Cliente
          </Heading>
          <Badge color="blue">{selectedYear}</Badge>
        </div>
        <Text className="text-azul-surelia/70">
          Análisis de tarifas promedio por cliente en el periodo seleccionado.
        </Text>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center" data-pdf-hide>
        <div className="flex items-center gap-2">
          <Text className="text-sm text-azul-surelia/80">Mes</Text>
          <select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value === "all" ? "all" : Number(e.target.value))}
            className="text-sm border rounded-lg px-3 py-2"
          >
            <option value="all">Todo el año</option>
            {monthNames.map((name, idx) => (
              <option key={name} value={idx}>
                {name.charAt(0).toUpperCase() + name.slice(1)}
              </option>
            ))}
          </select>
        </div>

        {/* Summary metrics */}
        <div className="flex gap-3 ml-auto">
          <div className="bg-azul-surelia/5 rounded-lg px-3 py-2 text-center">
            <div className="text-xs text-azul-surelia/60">Tarifa promedio</div>
            <div className="text-lg font-bold text-azul-surelia">{formatPercent(globalAvgTarifa)}</div>
          </div>
          <div className="bg-zinc-100 rounded-lg px-3 py-2 text-center">
            <div className="text-xs text-zinc-500">Clientes</div>
            <div className="text-lg font-bold text-zinc-900">{resumenPorCliente.length}</div>
          </div>
          <div className="bg-zinc-100 rounded-lg px-3 py-2 text-center">
            <div className="text-xs text-zinc-500">Fianzas</div>
            <div className="text-lg font-bold text-zinc-900">{filteredFianzas.length}</div>
          </div>
          {filteredFianzas.some((f) => f.primaMinima) && (
            <div className="bg-amber-50 rounded-lg px-3 py-2 text-center">
              <div className="text-xs text-amber-600">Prima mín.</div>
              <div className="text-lg font-bold text-amber-700">
                {filteredFianzas.filter((f) => f.primaMinima).length}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Chart */}
      {!loading && chartData.length > 0 && (
        <div className="bg-white border border-azul-surelia/15 rounded-xl shadow-sm overflow-hidden">
          <div className="p-4 border-b border-zinc-200">
            <Heading level={3} className="text-xl text-azul-surelia">
              Tarifa promedio por cliente
            </Heading>
            <Text className="text-sm text-azul-surelia/60 mt-1">
              Top 15 clientes - {selectedMonth === "all" ? "año completo" : monthNames[selectedMonth]} {selectedYear}
            </Text>
          </div>
          <div className="p-4">
            <ResponsiveContainer width="100%" height={400}>
              <BarChart
                data={chartData}
                layout="vertical"
                margin={{ top: 25, right: 30, left: 10, bottom: 10 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" horizontal={true} vertical={false} />
                <XAxis
                  type="number"
                  tickFormatter={(value) => `${value.toFixed(1)}%`}
                  tick={{ fill: '#284865', fontSize: 11 }}
                  axisLine={{ stroke: '#d1d5db' }}
                  domain={[0, 'auto']}
                />
                <YAxis
                  type="category"
                  dataKey="name"
                  tick={{ fill: '#284865', fontSize: 11 }}
                  axisLine={{ stroke: '#d1d5db' }}
                  width={150}
                />
                <Tooltip
                  content={({ payload }) => {
                    if (!payload || !payload.length) return null;
                    const data = payload[0].payload;
                    return (
                      <div className="bg-white border border-zinc-200 rounded-lg shadow-lg p-3">
                        <p className="font-semibold text-azul-surelia text-sm mb-2">{data.fullName}</p>
                        <div className="space-y-1 text-xs">
                          <p className="flex justify-between gap-4">
                            <span className="text-zinc-500">Tarifa promedio:</span>
                            <span className="font-semibold">{formatPercent(data.avgTarifa)}</span>
                          </p>
                          <p className="flex justify-between gap-4">
                            <span className="text-zinc-500">Fianzas:</span>
                            <span className="font-semibold">{data.fianzas}</span>
                          </p>
                        </div>
                      </div>
                    );
                  }}
                />
                <ReferenceLine
                  x={globalAvgTarifa}
                  stroke="#ef4444"
                  strokeDasharray="5 5"
                  strokeWidth={2}
                  label={{
                    value: `Prom: ${formatPercent(globalAvgTarifa)}`,
                    position: 'top',
                    fill: '#ef4444',
                    fontSize: 11,
                    fontWeight: 600,
                  }}
                />
                <Bar dataKey="avgTarifa" radius={[0, 4, 4, 0]}>
                  {chartData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={getTarifaColor(entry.avgTarifa, globalAvgTarifa)}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            <div className="flex justify-center gap-4 mt-2 text-xs">
              <span className="flex items-center gap-1">
                <span className="w-3 h-3 rounded bg-green-500"></span>
                <span className="text-zinc-600">Arriba del promedio</span>
              </span>
              <span className="flex items-center gap-1">
                <span className="w-3 h-3 rounded bg-azul-surelia"></span>
                <span className="text-zinc-600">Cerca del promedio</span>
              </span>
              <span className="flex items-center gap-1">
                <span className="w-3 h-3 rounded bg-amber-500"></span>
                <span className="text-zinc-600">Bajo el promedio</span>
              </span>
              <span className="flex items-center gap-1">
                <span className="w-3 h-3 rounded bg-red-500"></span>
                <span className="text-zinc-600">Muy bajo</span>
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Summary Table */}
      <div className="bg-white border border-azul-surelia/15 rounded-xl shadow-sm overflow-hidden">
        <div className="p-4 border-b border-zinc-200 flex items-center justify-between">
          <Heading level={3} className="text-xl text-azul-surelia">
            Detalle por cliente
          </Heading>
          <Badge color="zinc">
            Período: {selectedMonth === "all" ? "Año completo" : monthNames[selectedMonth]} {selectedYear}
          </Badge>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHead className="bg-azul-surelia text-white">
              <TableRow>
                <TableHeader className="text-white w-8"></TableHeader>
                <TableHeader className="text-white">Cliente</TableHeader>
                <TableHeader className="text-white text-center">Fianzas</TableHeader>
                <TableHeader className="text-white text-right">Prima Total</TableHeader>
                <TableHeader className="text-white text-right">Monto Afianzado</TableHeader>
                <TableHeader className="text-white text-center">Tarifa Prom.</TableHeader>
                <TableHeader className="text-white text-center">Rango</TableHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan="7" className="py-8 text-center text-azul-surelia/70">
                    Cargando datos…
                  </TableCell>
                </TableRow>
              ) : resumenPorCliente.length === 0 ? (
                <TableRow>
                  <TableCell colSpan="7" className="py-8 text-center text-azul-surelia/70">
                    No hay datos para este periodo.
                  </TableCell>
                </TableRow>
              ) : (
                resumenPorCliente.map((row, index) => {
                  const isExpanded = expandedRows.has(row.id);
                  const clientFianzas = getFianzasForClient(row.id);
                  const tarifaColor = getTarifaColor(row.avgTarifa, globalAvgTarifa);

                  return (
                    <React.Fragment key={row.id}>
                      <TableRow
                        className={`transition-colors hover:bg-azul-surelia/5 cursor-pointer ${
                          index % 2 === 0 ? "bg-white" : "bg-zinc-50/50"
                        } ${isExpanded ? "bg-azul-surelia/5" : ""}`}
                        onClick={() => toggleRowExpanded(row.id)}
                      >
                        <TableCell className="py-3 w-8">
                          <ChevronDownIcon
                            className={`w-5 h-5 text-azul-surelia/60 transition-transform ${
                              isExpanded ? "rotate-0" : "-rotate-90"
                            }`}
                          />
                        </TableCell>
                        <TableCell className="py-3">
                          <span className="font-medium text-zinc-900">{row.name}</span>
                        </TableCell>
                        <TableCell className="text-center py-3 text-zinc-700">
                          {row.fianzasCount}
                          {row.primaMinimaCount > 0 && (
                            <span className="ml-1 text-[10px] text-amber-600" title={`${row.primaMinimaCount} prima mínima excluida${row.primaMinimaCount > 1 ? 's' : ''} del promedio`}>
                              ({row.primaMinimaCount} p.m.)
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-right py-3 text-zinc-700">
                          {formatCurrency(row.primaTotal)}
                        </TableCell>
                        <TableCell className="text-right py-3 text-zinc-700">
                          {formatCurrency(row.montoTotal)}
                        </TableCell>
                        <TableCell className="text-center py-3">
                          <span
                            className="inline-flex items-center px-2.5 py-1 rounded-full text-sm font-semibold"
                            style={{ backgroundColor: `${tarifaColor}20`, color: tarifaColor }}
                          >
                            {formatPercent(row.avgTarifa)}
                          </span>
                        </TableCell>
                        <TableCell className="text-center py-3 text-zinc-600 text-sm">
                          {formatPercent(row.minTarifa)} - {formatPercent(row.maxTarifa)}
                        </TableCell>
                      </TableRow>
                      {/* Expanded fianzas list */}
                      {isExpanded && clientFianzas.map((fianza) => {
                        const fianzaTarifaColor = getTarifaColor(fianza.fianzaTarifa || 0, globalAvgTarifa);
                        return (
                          <TableRow
                            key={fianza.id}
                            className="bg-zinc-50/80 hover:bg-azul-surelia/5 cursor-pointer transition-colors"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedFianza(fianza);
                            }}
                          >
                            <TableCell className="py-2 w-8"></TableCell>
                            <TableCell className="py-2 pl-10">
                              <span className="text-sm text-azul-surelia/80 hover:text-azul-surelia hover:underline">
                                {formatNumeroFianza(fianza, "Sin número")}
                              </span>
                            </TableCell>
                            <TableCell className="text-center py-2 text-zinc-500 text-sm">
                              {fianza.inicioVigenciaMs
                                ? new Date(fianza.inicioVigenciaMs).toLocaleDateString("es-MX", { day: "2-digit", month: "short" })
                                : "-"}
                            </TableCell>
                            <TableCell className="text-right py-2 text-zinc-600 text-sm">
                              {formatCurrency(fianza.primaAmount || 0)}
                            </TableCell>
                            <TableCell className="text-right py-2 text-zinc-600 text-sm">
                              {formatCurrency(fianza.montoAfianzado || 0)}
                            </TableCell>
                            <TableCell className="text-center py-2">
                              <span
                                className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
                                style={{ backgroundColor: `${fianzaTarifaColor}15`, color: fianzaTarifaColor }}
                              >
                                {formatPercent(fianza.fianzaTarifa || 0)}
                                {fianza.primaMinima && " · Prima mín."}
                              </span>
                            </TableCell>
                            <TableCell className="text-center py-2 text-zinc-500 text-xs">
                              {fianza.comisionPct !== null ? `Com: ${fianza.comisionPct}%` : "-"}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </React.Fragment>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* FianzaView modal */}
      {selectedFianza && createPortal(
        <div className="relative z-[70]">
          <FianzaView
            isOpen={!!selectedFianza}
            onClose={() => setSelectedFianza(null)}
            fianza={selectedFianza}
            clientData={getClientDataForFianza(selectedFianza)}
            contratoData={getContractDataForFianza(selectedFianza)}
          />
        </div>,
        document.body
      )}
    </div>
  );
}
